import { getPostalCodeRule } from './postalCodeRules';

const validateAndorraPostalCode = (
  postalCode: string
): { isValid: boolean; error?: string; formatted?: string } => {
  const cleaned = postalCode.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
  const rule = getPostalCodeRule('AD');
  if (cleaned.length > 5) {
    return {
      isValid: false,
      error: 'Invalid format. Expected: AD###',
      formatted: postalCode.toUpperCase(),
    };
  }
  if (cleaned.length === 5) {
    const formatted = cleaned;
    if (rule?.pattern && /^AD\d{3}$/.test(formatted)) {
      return { isValid: true, formatted };
    }
    return {
      isValid: false,
      error: 'Invalid format. Expected: AD###',
      formatted,
    };
  }
  return { isValid: true, formatted: cleaned };
};

const validateBermudaPostalCode = (
  postalCode: string
): { isValid: boolean; error?: string; formatted?: string } => {
  const cleaned = postalCode.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
  const rule = getPostalCodeRule('BM');
  if (cleaned.length > 4) {
    return {
      isValid: false,
      error: 'Invalid format. Expected: AA ## or AA AA',
      formatted: postalCode.toUpperCase(),
    };
  }
  if (cleaned.length === 4) {
    const formatted = `${cleaned.slice(0, 2)} ${cleaned.slice(2)}`;
    if (rule?.pattern?.test(formatted)) {
      return { isValid: true, formatted };
    }
    return {
      isValid: false,
      error: 'Invalid format. Expected: AA ## or AA AA',
      formatted,
    };
  }
  return { isValid: true, formatted: cleaned };
};

const validateSwedishPostalCode = (
  postalCode: string
): { isValid: boolean; error?: string; formatted?: string } => {
  const cleaned = postalCode.replace(/\D/g, '');
  const rule = getPostalCodeRule('SE');
  if (cleaned.length > 5) {
    return {
      isValid: false,
      error: 'Invalid format. Expected: ### ##',
      formatted: postalCode,
    };
  }
  if (cleaned.length === 5) {
    const formatted = `${cleaned.slice(0, 3)} ${cleaned.slice(3)}`;
    if (rule?.pattern?.test(formatted)) {
      return { isValid: true, formatted };
    }
    return {
      isValid: false,
      error: 'Invalid format. Expected: ### ##',
      formatted,
    };
  }
  return { isValid: true, formatted: postalCode };
};

const validateBrazilianPostalCode = (
  postalCode: string
): { isValid: boolean; error?: string; formatted?: string } => {
  const cleaned = postalCode.replace(/\D/g, '');
  const rule = getPostalCodeRule('BR');
  if (cleaned.length > 8) {
    return {
      isValid: false,
      error: 'Invalid format. Expected: #####-###',
      formatted: postalCode,
    };
  }
  if (cleaned.length === 8) {
    const formatted = `${cleaned.slice(0, 5)}-${cleaned.slice(5)}`;
    if (rule?.pattern?.test(formatted)) {
      return { isValid: true, formatted };
    }
    return {
      isValid: false,
      error: 'Invalid format. Expected: #####-###',
      formatted,
    };
  }
  return { isValid: true, formatted: postalCode };
};

const validateCanadianPostalCode = (
  postalCode: string
): { isValid: boolean; error?: string; formatted?: string } => {
  const cleaned = clearZipCode(postalCode);
  const formatted = formatCanadianZipCode(cleaned);
  if (cleaned.length > 6) {
    return {
      isValid: false,
      error: 'Invalid format. Expected: A1A 1A1',
      formatted,
    };
  }
  if (cleaned.length === 6) {
    if (validateCanadianZipCode(formatted)) {
      return { isValid: true, formatted };
    }
    return {
      isValid: false,
      error: 'Invalid format. Expected: A1A 1A1',
      formatted,
    };
  }
  return { isValid: true, formatted };
};

const validateUKPostalCode = (
  postalCode: string
): { isValid: boolean; error?: string; formatted?: string } => {
  const cleaned = postalCode.replace(/\s+/g, '').toUpperCase();
  const rule = getPostalCodeRule('GB');

  if (cleaned.length > 7) {
    return {
      isValid: false,
      error: 'Invalid format. Expected: UK format (e.g., SW1A 1AA)',
      formatted: postalCode.toUpperCase(),
    };
  }

  // Format UK postcode: outward code + space + inward code (1 digit + 2 letters)
  if (cleaned.length >= 5) {
    const inwardStart = cleaned.length - 3;
    const outward = cleaned.slice(0, inwardStart);
    const inward = cleaned.slice(inwardStart);
    const formatted = `${outward} ${inward}`;

    if (cleaned.length === 6 || cleaned.length === 7) {
      if (rule?.pattern?.test(formatted)) {
        return { isValid: true, formatted };
      }
      return {
        isValid: false,
        error: 'Invalid format. Expected: UK format (e.g., SW1A 1AA)',
        formatted,
      };
    }

    return { isValid: true, formatted };
  }

  return { isValid: true, formatted: cleaned };
};

export const clearZipCode = (zipCode: string) => {
  return zipCode
    .replace(/[^A-Za-z0-9]/g, '')
    .trim()
    .toUpperCase();
};

export const formatCanadianZipCode = (zipCode: string) => {
  if (!zipCode) return zipCode;

  const cleanZipCode = clearZipCode(zipCode);

  if (cleanZipCode.length >= 3) {
    return `${cleanZipCode.slice(0, 3)} ${cleanZipCode.slice(3)}`.trim();
  }

  return cleanZipCode;
};

export const validateCanadianZipCode = (zipCode: string) => {
  if (!zipCode) return true;
  const formattedZipcode = formatCanadianZipCode(zipCode);
  const rule = getPostalCodeRule('CA');

  return rule?.pattern?.test(formattedZipcode) || false;
};

export {
  validateAndorraPostalCode,
  validateBermudaPostalCode,
  validateSwedishPostalCode,
  validateBrazilianPostalCode,
  validateCanadianPostalCode,
  validateUKPostalCode,
};
